import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Autocomplete, Avatar, Box, Button, Card, CardActionArea, CardContent, CircularProgress, FormControlLabel, Grid, IconButton, Stack, Switch, TextField, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { fetchBoardById, updateBoard } from '../../controllers/boardsController';
import { searchMembers } from '../../controllers/memberManagementController';
import { resolveGroupsAssetUrl } from '../../api/httpClientGroups';

const BoardDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [item, setItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);
  const [memberQuery, setMemberQuery] = useState('');
  const [memberOptions, setMemberOptions] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchBoardById(id);
      setItem(data);
    } catch (e) {
      setError(e?.message || 'Kayıt alınamadı');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!memberQuery || memberQuery.length < 2) { setMemberOptions([]); return; }
      try {
        const data = await searchMembers(memberQuery);
        if (!active) return;
        // data is an array of { id, fullName, profilePictureUrl }
        setMemberOptions(data || []);
      } catch (e) {}
    })();
    return () => { active = false; };
  }, [memberQuery]);

  const memberDisplay = useMemo(() => (item?.members || []).map((m) => ({ id: m.id, fullName: m.fullName, profilePictureUrl: m.profilePictureUrl })), [item]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  if (error) return <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>;
  if (!item) return null;

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>Kurul Detayı</Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Stack spacing={1.25}>
                <TextField label="Başlık" size="small" value={item.title} onChange={(e) => setItem({ ...item, title: e.target.value })} />
                <FormControlLabel control={<Switch checked={!!item.isActive} onChange={(e) => setItem({ ...item, isActive: e.target.checked })} />} label="Aktif" />
                <Stack direction="row" spacing={1}>
                  <Button variant="contained" disabled={saving} onClick={async () => {
                    setSaving(true);
                    try {
                      await updateBoard({ id: item.id, title: item.title, members: item.members || [], isActive: item.isActive });
                    } catch (e) {
                    } finally {
                      setSaving(false);
                      await load();
                    }
                  }}>Kaydet</Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 700 }}>Üye Ekle</Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <Autocomplete
                  sx={{ flex: 1, minWidth: 520 }}
                  size="medium"
                  value={selectedMember}
                  onChange={(e, v) => setSelectedMember(v)}
                  onInputChange={(e, v) => setMemberQuery(v)}
                  options={memberOptions}
                  getOptionLabel={(o) => (o ? o.fullName || '' : '')}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar src={resolveGroupsAssetUrl(option.profilePictureUrl)} sx={{ width: 28, height: 28 }} />
                      {option.fullName}
                    </Box>
                  )}
                  slotProps={{ paper: { sx: { width: 560 } }, listbox: { sx: { maxHeight: 360 } } }}
                  renderInput={(params) => <TextField {...params} fullWidth label="Üye ara" placeholder="İsim yazın..." />}
                />
                <Button variant="contained" disabled={!selectedMember || adding} onClick={async () => {
                  if (!selectedMember) return;
                  setAdding(true);
                  try {
                    const fullName = selectedMember.fullName || '';
                    const newMember = { id: selectedMember.id, fullName, profilePictureUrl: selectedMember.profilePictureUrl || null };
                    const newMembers = [...(item.members || []), newMember];
                    await updateBoard({ id: item.id, title: item.title, members: newMembers, isActive: item.isActive });
                    setSelectedMember(null);
                    await load();
                  } catch (e) {
                  } finally {
                    setAdding(false);
                  }
                }}>{adding ? 'Ekleniyor...' : 'Ekle'}</Button>
              </Stack>
            </CardContent>
          </Card>
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700 }}>Üyeler</Typography>
              <Grid container spacing={1}>
                {memberDisplay.map((m) => (
                  <Grid item key={m.id} xs={12} sm={6} md={4} lg={3}>
                    <Card elevation={1} sx={{ height: 260, position: 'relative' }}>
                      <CardActionArea
                        onClick={() => navigate(`/members/${m.id}`)}
                        sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', p: 1, '&:hover .underline': { borderBottomColor: 'primary.main' } }}
                      >
                        <Box sx={{ width: '100%', height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default', overflow: 'hidden', borderRadius: 1 }}>
                          <Box component="img" src={resolveGroupsAssetUrl(m.profilePictureUrl)} alt={m.fullName} sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'center', mt: 1 }}>{m.fullName}</Typography>
                        <Box className="underline" sx={{ width: '100%', borderBottom: '2px solid', borderBottomColor: 'transparent', mt: 0.5 }} />
                      </CardActionArea>
                      <IconButton size="small" color="error" sx={{ position: 'absolute', top: 4, right: 4, zIndex: 2 }} onClick={async (e) => {
                        e.stopPropagation();
                        const newMembers = (item.members || []).filter((x) => x.id !== m.id);
                        try {
                          await updateBoard({ id: item.id, title: item.title, members: newMembers, isActive: item.isActive });
                          await load();
                        } catch (e) {}
                      }}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BoardDetail;



